import React from 'react';
import { Badge, Button, Modal, ProgressBar } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import alertIcon from '../../../assets/SidebarIcons/icon-alert-warning.svg';
import calendarIcon from '../../../assets/SidebarIcons/icon-calendar-time.svg';
import { formatWithoutCurrency, hideCostForUser } from '../../../helpers/helperFunction';
import CloseIcon from '../../SVG/CloseIcon';
import CampaignPhone from './campaignPhone';

const white_label_perkeso = process.env.REACT_APP_WHITE_LABEL_PERKESO === 'true';

const CampaignDetails = ({
  campaignDetailsData,
  campaignStatus,
  modalDetailShow,
  setModalDetailShow,
  resumeCampaign,
}) => {
  const navigate = useNavigate();
  const { email } = useSelector((state) => state.auth.user);
  const hide_cost = hideCostForUser(email);
  const {
    id,
    delivered,
    delivered_percentage,
    failed,
    failed_percentage,
    sent,
    sent_percentage,
    message_content,
    total_cost,
    cost_per_sms,
    delivered_date,
    starts_at,
    campaign_name,
    contact_list_name,
    sms_message_count,
    message_character_count,
    message_encoding
  } = campaignDetailsData;

  const handleModalDetailClose = () => {
    setModalDetailShow(false);
  };

  const handleEdit = () => {
    const campaign_data = encodeURIComponent(JSON.stringify(campaignDetailsData));
    navigate(`/dashboard/campaign/campaign-create/${campaign_data}`);
  };

  const handleResume = (id) => {
    resumeCampaign(id);
    setModalDetailShow(false);
  }

  const renderCampaignStatus = () => {
    const statusMap = {
      completed: {
        color: '#1faf38',
        background: 'rgba(31,175,56,0.2)',
        label: 'Completed',
      },
      failed: {
        color: '#de0d19',
        background: 'rgba(222,13,25,0.2)',
        label: 'Failed',
      },
      draft: {
        color: '#b3b3b3',
        background: 'rgba(179,179,179,0.2)',
        label: 'Draft',
      },
      scheduled: {
        color: '#ffa047',
        background: 'rgba(255,160,71,0.2)',
        label: 'Scheduled',
      },
      in_progress: {
        color: '#1bf907',
        background: 'rgba(27,249,7,0.2)',
        label: 'In-Progress',
      },
      incomplete: {
        color: '#f15a24',
        background: 'rgba(241,90,36,0.2)',
        label: 'Incomplete',
      },
    };

    const { color, background, label } = statusMap[campaignStatus] || {};

    return (
      <Badge style={{ color, background }}>{label}</Badge>
    );
  };

  const renderDeliveryTime = () => {
    switch (campaignStatus) {
      case 'failed':
        return (
          <>
            <div className="delivery-time-title">
              <img src={alertIcon} alt="..." />
              Your SMS campaign encountered issues.
            </div>
            <div className="delivery-time">
              <strong>Possible reasons include</strong>
              <ul>
                <li>Blacklist words</li>
                <li>Brand names</li>
                <li>Incorrect mobile number formats</li>
              </ul>
              Review and address these issues to proceed.
            </div>
          </>
        );
      case 'draft':
        return (
          <>
            <div className="delivery-time-title">
              <img src={alertIcon} alt="..." />
              Your SMS campaign is not live.
            </div>
            <div className="delivery-time">
              Your campaign is still under Draft. Click Send Now to push the campaign Live
              <br />
              <Button
                variant="primary"
                style={{ minWidth: 140, marginTop: 16 }}
                onClick={handleEdit}
              >
                Continue Editing
              </Button>
            </div>
          </>
        );
      case 'incomplete':
        return (
          <>
            <div className="delivery-time-title">
              <img src={calendarIcon} alt="..." />
              Scheduled Time
            </div>
            <div className="delivery-time">{starts_at}
              <br />
              <Button
                variant="primary"
                style={{ minWidth: 140, marginTop: 16 }}
                onClick={() => handleResume(id)}
              >
                Resume
              </Button>
            </div>
          </>
        );
      default:
        return (
          <>
            <div className="delivery-time-title">
              <img src={calendarIcon} alt="..." />
              Scheduled Time
            </div>
            <div className="delivery-time">{starts_at}</div>
          </>
        );
    }
  };

  const renderAnalytics = () => (
    <>
      <h4 style={{ margin: '8px 0 38px' }}>Analytics</h4>
      <div>
        <ProgressBar
          variant="success"
          now={delivered_percentage}
          label={
            <span>
              <strong>Delivered</strong> ({delivered})
            </span>
          }
        />
        <ProgressBar
          variant="primary"
          now={sent_percentage}
          label={
            <span>
              <strong>Sent</strong> ({sent})
            </span>
          }
        />
        <ProgressBar
          variant="danger"
          now={failed_percentage}
          label={
            <span>
              <strong>Failed</strong> ({failed})
            </span>
          }
        />
      </div>
    </>
  );

  return (
    <Modal
      show={modalDetailShow}
      animation={true}
      className="modal-overview-detail"
      onHide={handleModalDetailClose}
    >
      <Modal.Body>
        <CloseIcon handleModalClose={handleModalDetailClose} />
        {renderCampaignStatus()}
        <Modal.Title as="h4" style={{ margin: '8px 0 32px' }}>
          {campaign_name}
        </Modal.Title>
        <div className="delivery-time-wrapper">{renderDeliveryTime()}</div>
        {campaignStatus !== 'failed' && campaignStatus !== 'draft' && (
          <>
            <h4 style={{ margin: '24px 0 16px' }}>
              Contact List: <span style={{ wordWrap: 'break-word' }}>{contact_list_name}</span>
            </h4>
            <p>SMS Encoding: {message_encoding}</p>
            <div className="d-flex justify-content-between align-items-center">
              <h6>{message_encoding} Characters:</h6>
              <p>{message_character_count}</p>
            </div>
            {renderAnalytics()}
          </>
        )}
        <h4 style={{ margin: '24px 0 16px' }}>Cost</h4>
        <div className="d-flex justify-content-between align-items-center">
          <h6>Total Message Sent:</h6>
          <p>{sent}</p>
        </div>
        <div className="d-flex justify-content-between align-items-center">
          <h6>Message Count:</h6>
          <p>{sms_message_count}</p>
        </div>
        { !white_label_perkeso && (
          <>
            <div className="d-flex justify-content-between align-items-center">
              <h6>SMS Rate</h6>
              <p>{formatWithoutCurrency(cost_per_sms, 3)}</p>
            </div>
            {!hide_cost && (
              <div className="d-flex justify-content-between align-items-center">
                <h6>Campaign Cost</h6>
                <p>{total_cost}</p>
              </div>
            )}
          </>
        )}
        <h4 style={{ margin: '24px 0 16px' }}>Message Preview</h4>

        <div className="mobile-frame">
          <CampaignPhone name={campaign_name} message={message_content} />
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default CampaignDetails;
